import {
  KeyboardArrowDown,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  KeyboardArrowUp,
} from '@mui/icons-material';
import {
  FormControl,
  FormLabel,
  InputAdornment,
  SxProps,
  OutlinedInput,
  Menu,
  Stack,
  Typography,
  IconButton,
  Box,
  PopoverOrigin,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import Button from '~/components/common/Button';
import { useEffect, useMemo, useRef, useState } from 'react';
import themes from '~/themes';
import moment from 'moment';
import CloseIcon from '~/assets/images/icons/close.svg';
import momentTz from 'moment-timezone';
import { useAuth } from '~/providers/AuthProvider';
import { TIMEZONE_SETTING } from '~/constants/adminOptions';
import { DEFAULT_TIMEZONE } from '~/constants/common';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateField, LocalizationProvider } from '@mui/x-date-pickers';
import DateRange from '@wojtekmaj/react-daterange-picker';
import 'react-calendar/dist/Calendar.css';
import './style.scss';

export interface TimeSelectorProps {
  value?: null | {
    from: string;
    to: string;
  };
  legend?: string;
  onChange?: (value: { from: string; to: string }) => void;
  onClear?: () => void;
  placeholder?: string;
  fullWidth?: boolean;
  sxLabel?: SxProps;
  hideLegend?: boolean;
  sxInput?: SxProps<Theme>;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
}

const getDateOptions = (timezone: string) => [
  {
    value: 'today',
    label: 'Today',
    getRange: () => {
      const today = moment.tz(timezone);
      return [today, today];
    },
  },
  {
    value: 'yesterday',
    label: 'Yesterday',
    getRange: () => {
      const yst = moment.tz(timezone).subtract(1, 'days');
      return [yst, yst];
    },
  },
  {
    value: 'last7',
    label: 'Last 7 days',
    getRange: () => {
      const end = moment.tz(timezone).subtract(1, 'days');
      const start = end.clone().subtract(6, 'days');
      return [start, end];
    },
  },
  {
    value: 'last14',
    label: 'Last 14 days',
    getRange: () => {
      const end = moment.tz(timezone).subtract(1, 'days');
      const start = end.clone().subtract(13, 'days');
      return [start, end];
    },
  },
  {
    value: 'last30',
    label: 'Last 30 days',
    getRange: () => {
      const end = moment.tz(timezone).subtract(1, 'days');
      const start = end.clone().subtract(29, 'days');
      return [start, end];
    },
  },
  {
    value: 'weekToDate',
    label: 'Week to date',
    getRange: () => {
      const end = moment.tz(timezone);
      const start = end.clone().startOf('week');
      return [start, end];
    },
  },
  {
    value: 'monthToDate',
    label: 'Month to date',
    getRange: () => {
      const end = moment.tz(timezone);
      const start = end.clone().startOf('month');
      return [start, end];
    },
  },
  {
    value: 'yearToDate',
    label: 'Year to date',
    getRange: () => {
      const end = moment.tz(timezone);
      const start = end.clone().startOf('year');
      return [start, end];
    },
  },
];

export const DateRangePicker: React.FC<TimeSelectorProps> = ({
  value,
  legend,
  placeholder = 'Date',
  onChange,
  onClear,
  fullWidth = true,
  sxLabel,
  hideLegend = false,
  sxInput,
  anchorOrigin,
  transformOrigin,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const anchorElRef = useRef(null);
  const { account } = useAuth();

  const timezone = useMemo(() => {
    if (account.timezone) {
      if (account.timezone === TIMEZONE_SETTING.AUTO_DETECT) {
        let tz = momentTz.tz.guess();
        if (tz === 'Asia/Saigon') {
          tz = 'Asia/Ho_Chi_Minh';
        }
        return tz;
      }
      return account.timezone;
    }
    return DEFAULT_TIMEZONE;
  }, [account.org]);

  const DateOption = useMemo(() => getDateOptions(timezone), [timezone]);

  const nowTzDate = useMemo(() => {
    const now = moment().tz(timezone);
    return new Date(
      now.year(),
      now.month(),
      now.date(),
      now.hour(),
      now.minute(),
      now.second(),
    );
  }, [timezone, value]);

  const convertToDateObject = (date: string) => {
    const momentDate = moment(date).tz(timezone);
    return new Date(
      momentDate.year(),
      momentDate.month(),
      momentDate.date(),
      momentDate.hour(),
      momentDate.minute(),
      momentDate.second(),
    );
  };

  const endDateRef = useRef<any>();
  const [dateRange, setDateRange] = useState<any>([nowTzDate, nowTzDate]);
  const [selectedOption, setSelectedOption] = useState<string | null>(
    DateOption[0].value,
  );
  const [activeStartDate, setActiveStartDate] = useState(moment());
  const [count, setCount] = useState(0);

  const findMatchingOption = (range: [Date, Date]) => {
    const isSameDay = (date1: Date, date2: Date) =>
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate();

    const [start, end] = range;
    const matched = DateOption.find((option) => {
      const [optStart, optEnd] = option.getRange();
      return (
        isSameDay(
          start,
          new Date(optStart.year(), optStart.month(), optStart.date()),
        ) &&
        isSameDay(end, new Date(optEnd.year(), optEnd.month(), optEnd.date()))
      );
    });

    return matched?.value ?? null;
  };

  const getValidDateRange = () => {
    const { from, to } = value;
    if (from && to) {
      const fromDate = convertToDateObject(from);
      const toDate = convertToDateObject(to);
      setDateRange([fromDate, toDate]);
    } else {
      setDateRange([nowTzDate, nowTzDate]);
    }
  };

  useEffect(() => {
    getValidDateRange();
  }, [value]);

  useEffect(() => {
    const [from, to] = dateRange;
    if (from && to) {
      setSelectedOption(findMatchingOption([from, to]));
    } else {
      setSelectedOption(DateOption[0].value);
    }
  }, [dateRange]);

  const handleNextMonth = () => {
    const nextMonth = moment(activeStartDate).add(1, 'months');
    setActiveStartDate(nextMonth);
  };

  const handlePrevMonth = () => {
    const prevMonth = moment(activeStartDate).subtract(1, 'months');
    setActiveStartDate(prevMonth);
  };

  const isValidRangeDate = useMemo(() => {
    const [startDate, endDate] = dateRange;
    const mStartDate = moment(startDate)?.local();
    const mEndDate = moment(endDate)?.local();

    if (
      mStartDate.isSameOrBefore(mEndDate, 'date') &&
      mEndDate.isSameOrBefore(moment(nowTzDate), 'date') &&
      mStartDate.isSameOrAfter(moment('2000/01/01'), 'date')
    )
      return true;
    return false;
  }, [dateRange]);

  const handleSubmit = () => {
    const startDate = moment(dateRange[0])?.local().format('YYYY-MM-DD');
    const endDate = moment(dateRange[1])?.local().format('YYYY-MM-DD');
    onChange({
      from: startDate,
      to: endDate,
    });
    setIsOpen(false);
  };

  const handleKeyDownStartDate = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      endDateRef.current?.focus();
    }
  };

  const handleKeyDownEndDate = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && isValidRangeDate) {
      handleSubmit();
    }
  };

  const displayValue = useMemo(() => {
    if (!value.from && !value.to) return '';
    if (selectedOption) {
      const option = DateOption.find((opt) => opt.value === selectedOption);
      return option?.label || '';
    }

    const currentYear = moment().year();
    const startMoment = moment(dateRange[0]).local();
    const endMoment = moment(dateRange[1]).local();

    const formatDate = (date: moment.Moment) =>
      date.year() === currentYear
        ? date.format('MMM D')
        : date.format('MMM D, YYYY');

    const start = formatDate(startMoment);
    const end = formatDate(endMoment);

    return start === end ? start : `${start} - ${end}`;
  }, [value, selectedOption, dateRange]);

  const renderEndAdornment = () => {
    if (displayValue && onClear)
      return (
        <InputAdornment
          position='end'
          variant='filled'
          sx={{
            paddingRight: '14px',
            paddingLeft: '14px',
          }}
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
        >
          <img alt='clear-icon' src={CloseIcon} width={10} height={11} />
        </InputAdornment>
      );

    return (
      <InputAdornment
        position='end'
        variant='filled'
        sx={{
          paddingRight: '7px',
        }}
      >
        {isOpen ? (
          <KeyboardArrowUp
            sx={{ color: `${themes.color.violet900} !important` }}
          />
        ) : (
          <KeyboardArrowDown
            sx={{ color: `${themes.color.violet900} !important` }}
          />
        )}
      </InputAdornment>
    );
  };

  return (
    <FormControl fullWidth={fullWidth} variant='standard'>
      {legend &&
        (typeof legend === 'string' ? (
          <FormLabel
            sx={{
              ...sxLabel,
              ...(hideLegend && {
                color: 'transparent !important',
              }),
            }}
          >
            {legend}
          </FormLabel>
        ) : (
          legend
        ))}
      <OutlinedInput
        value={displayValue}
        ref={anchorElRef}
        sx={{
          cursor: 'pointer',
          input: {
            cursor: 'pointer',
            overflow: 'hidden !important',
            textOverflow: 'ellipsis !important',
            whiteSpace: 'nowrap !important',
          },
          ...sxInput,
        }}
        readOnly
        placeholder={placeholder}
        endAdornment={renderEndAdornment()}
        onClick={() => {
          setIsOpen((prevState) => !prevState);
        }}
      />
      <Menu
        anchorEl={anchorElRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
          ...anchorOrigin,
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
          ...transformOrigin,
        }}
        classes={{ paper: 'custom-paper' }}
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
          getValidDateRange();
        }}
        MenuListProps={{
          sx: {
            padding: 0,
          },
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '8px',
              p: 2.4,
              boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.16)',
            },
          },
        }}
      >
        <Stack
          gap={2.4}
          flexDirection='row'
          sx={{
            maxWidth: 'calc(100vw - 80px)',
            overflowX: 'auto',
          }}
        >
          <Stack
            minWidth={140}
            sx={{
              display: 'flex',
              gap: 0.8,
              pt: 0.5,
            }}
          >
            {DateOption?.map((option) => (
              <Box
                key={option.value}
                sx={{
                  'display': 'flex',
                  'flexDirection': 'row',
                  'justifyContent': 'space-between',
                  'alignItems': 'center',
                  'p': '6px 8px',
                  'cursor': 'pointer',
                  '&:hover': {
                    backgroundColor: '#EDF0F7',
                    borderRadius: '100px',
                  },
                  ...(selectedOption === option.value && {
                    backgroundColor: '#EDF0F7',
                    borderRadius: '100px',
                  }),
                }}
                onClick={() => {
                  const [start, end] = option.getRange();
                  setDateRange([
                    convertToDateObject(start.toString()),
                    convertToDateObject(end.toString()),
                  ]);
                  setSelectedOption(option.value);
                  setActiveStartDate(moment(end));
                  setCount((prev) => prev + 1);
                }}
              >
                <Typography>{option.label}</Typography>
                {selectedOption === option.value && (
                  <KeyboardArrowRight
                    sx={{
                      fontSize: 18,
                    }}
                  />
                )}
              </Box>
            ))}
          </Stack>
          <Stack>
            <Stack
              sx={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  color: '#33363D',
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                {activeStartDate?.format('MMMM YYYY')}{' '}
              </Typography>
              <Stack
                sx={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <IconButton
                  onClick={handlePrevMonth}
                  sx={{
                    color: '#33363D',
                  }}
                >
                  <KeyboardArrowLeft />
                </IconButton>
                <IconButton
                  onClick={handleNextMonth}
                  sx={{
                    color: '#33363D',
                  }}
                  disabled={moment(activeStartDate).isSame(moment(), 'months')}
                >
                  <KeyboardArrowRight />
                </IconButton>
              </Stack>
            </Stack>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Stack
                sx={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1.6,
                  mt: 0.5,
                }}
              >
                <Stack
                  sx={{
                    flexDirection: 'column',
                    gap: '5px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: '#33363D',
                    }}
                  >
                    From date
                  </Typography>
                  <DateField
                    label=''
                    format='MM/DD/YYYY'
                    value={moment(dateRange[0])?.local()}
                    disableFuture
                    maxDate={moment(nowTzDate)}
                    minDate={moment('2000-01-01')}
                    onChange={(newValue) => {
                      const newStartDate = moment(newValue);
                      const endDate = dateRange[1];
                      if (newStartDate.isValid()) {
                        setCount((prev) => prev + 1);
                        setDateRange([newStartDate.toDate(), endDate]);
                      }
                    }}
                    onKeyDown={handleKeyDownStartDate}
                  />
                </Stack>
                <Stack
                  sx={{
                    flexDirection: 'column',
                    gap: '5px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: '#33363D',
                    }}
                  >
                    To date
                  </Typography>
                  <DateField
                    label=''
                    format='MM/DD/YYYY'
                    value={moment(dateRange[1])?.local()}
                    disableFuture
                    minDate={moment('2000-01-01')}
                    maxDate={moment(nowTzDate)}
                    onChange={(newValue) => {
                      const startDate = dateRange[0];
                      const newEndDate = moment(newValue);
                      if (newEndDate.isValid()) {
                        setCount((prev) => prev + 1);
                        setDateRange([startDate, newEndDate.toDate()]);
                      }
                    }}
                    onKeyDown={handleKeyDownEndDate}
                    inputRef={endDateRef}
                  />
                </Stack>
              </Stack>
            </LocalizationProvider>
            <Stack
              sx={{
                width: '350px',
                aspectRatio: '1/1.1',
              }}
            >
              <DateRange
                key={`${count}`}
                className='custom-date-range'
                isOpen
                calendarIcon={null}
                clearIcon={null}
                onChange={(newDateRange) => {
                  setDateRange(newDateRange);
                  if (newDateRange[1]) {
                    setActiveStartDate(moment(newDateRange[1]));
                  } else if (newDateRange[0]) {
                    setActiveStartDate(moment(newDateRange[0]));
                  }
                }}
                value={dateRange}
                closeCalendar={false}
                maxDate={nowTzDate}
                calendarProps={{
                  activeStartDate: activeStartDate?.toDate(),
                  onActiveStartDateChange: ({ value: newValue }: any) => {
                    setDateRange([
                      moment(newValue).local().toDate(),
                      moment(newValue).local().toDate(),
                    ]);
                  },
                  formatShortWeekday: (_, date) =>
                    moment(date).local().format('dd'),
                }}
              />
            </Stack>
            <Stack
              sx={{
                flexDirection: 'row',
                gap: 1.2,
                justifyContent: 'flex-end',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <Button
                sx={{
                  'color': '#121142',
                  'width': '70px',
                  'backgroundColor': 'white',
                  'minWidth': 'unset !important',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    textDecoration: 'underline',
                  },
                }}
                onClick={() => {
                  setDateRange([nowTzDate, nowTzDate]);
                  setActiveStartDate(moment());
                  setSelectedOption(DateOption[0].value);
                  setCount((prev) => prev + 1);
                }}
              >
                Clear
              </Button>
              <Button
                sx={{
                  'color': 'white',
                  'width': '70px',
                  'backgroundColor': '#121142',
                  'borderRadius': '6px',
                  'minWidth': 'unset !important',
                  '&:hover': {
                    backgroundColor: '#121142',
                    opacity: 0.8,
                  },
                }}
                onClick={handleSubmit}
                disabled={!isValidRangeDate}
              >
                Apply
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Menu>
    </FormControl>
  );
};
